import { Adapter } from '@mode-switch/adapt';
import { Field, Model, View } from '@mode-switch/model';
import { RecordsPlan, RecordsQuery } from '@mode-switch/query';
import { EMPTY_ENCODING, EncField, Encoding, VariableType } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, Switch } from '@mode/shared/util-switch';
import { escapeFieldName } from '@mode/shared/util-js';
import { batchEncoding, collectEncFields } from '../../chart-encoding-helpers';
import { makeField } from '../field';
import { makeFilters } from '../filter';
import { makeMarks } from '../mark';
import { makeTableSorts } from '../sort';

type ModeRole = FlamingoTypes.ModeRole;

export function buildRecordsPlan(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  limit: number,
  offset: number
): RecordsPlan<ModeRole> {
  const { query, aggregateFields, aggregateNames } = buildRecordsQueryWithAggregates(
    adapter,
    model,
    viewName,
    encoding,
    limit,
    offset
  );
  return RecordsPlan.make(adapter, model, viewName, query, aggregateFields, aggregateNames);
}

export function buildRecordsPlanWithAggregateCalcs(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  limit: number,
  offset: number
): RecordsPlan<ModeRole> {
  const { query, aggregateFields, aggregateNames } = buildRecordsQueryWithAggregates(
    adapter,
    model,
    viewName,
    encoding,
    limit,
    offset
  );
  return RecordsPlan.make(adapter, model, viewName, query, aggregateFields, aggregateNames);
}

export function buildRecordsPlanWithDetail(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  encDetail: EncField[] | undefined | null,
  limit: number,
  offset: number
): RecordsPlan<ModeRole> {
  let detail: Field<ModeRole>[] | undefined;
  if (encDetail?.length) {
    const detailMap = new Map<string, Field<ModeRole>>();
    detail = encDetail.map((encField) => {
      return makeField(model, viewName, EMPTY_ENCODING, detailMap, encField);
    });
  }

  const query = buildRecordsQuery(model, viewName, encoding, limit, offset);
  const queryWithDetail = RecordsQuery.make({ ...query, detail });
  return RecordsPlan.make(adapter, model, viewName, queryWithDetail);
}

export function buildRecordsPlanWithDetailAndAggregateCalcs(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  encDetail: EncField[] | undefined | null,
  limit: number,
  offset: number
): RecordsPlan<ModeRole> {
  let detail: Field<ModeRole>[] | undefined;
  if (encDetail?.length) {
    const detailMap = new Map<string, Field<ModeRole>>();
    detail = encDetail.map((encField) => {
      return makeField(model, viewName, EMPTY_ENCODING, detailMap, encField);
    });
  }

  const { query, aggregateFields, aggregateNames } = buildRecordsQueryWithAggregates(
    adapter,
    model,
    viewName,
    encoding,
    limit,
    offset
  );
  const queryWithDetail = RecordsQuery.make({ ...query, detail });
  return RecordsPlan.make(adapter, model, viewName, queryWithDetail, aggregateFields, aggregateNames);
}

/**
 * Result of building records query with separated aggregate fields
 */
interface RecordsQueryWithAggregates {
  query: RecordsQuery<ModeRole>;
  aggregateFields: Field<ModeRole>[];
  aggregateNames: Map<string, string>;
}

/** @internal */
function buildRecordsQueryWithAggregates(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  limit: number,
  offset: number
): RecordsQueryWithAggregates {
  const view = Switch.Views.find(model, viewName);
  if (view == null) {
    throw Error(`No view with name ${viewName} was found`);
  }

  // Separate fields into scalar, aggregate, and handle invalid formulas
  const separated = separateFieldsByType(adapter, model, view, viewName, encoding);

  // Update encoding with scalar fields only (aggregate fields removed, invalid fields replaced)
  encoding = separated.scalarEncoding;
  model = Switch.Models.make(viewName, [view]);

  const fieldMap = new Map<string, Field<ModeRole>>();
  if (encoding.marks.channel === '*') {
    makeMarks(model, viewName, encoding, fieldMap, encoding.marks.properties);
  }

  // Apply display names for fields that were modified
  const fields = Array.from(fieldMap.values()).map((field): Field<ModeRole> => {
    const name = separated.scalarNames.get(field.alias) ?? field.name;
    return {
      ...field,
      name,
    };
  });

  const sorts = makeTableSorts(model, viewName, encoding, fieldMap);
  const filters = makeFilters(model, viewName, encoding, fieldMap);

  const query = RecordsQuery.make({
    fields,
    sorts,
    filters,
    limit,
    offset,
  });

  return {
    query,
    aggregateFields: separated.aggregateFields,
    aggregateNames: separated.aggregateNames,
  };
}

/** @internal - kept for backward compatibility */
export function buildRecordsQuery(
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  limit: number,
  offset: number
): RecordsQuery<ModeRole> {
  const view = Switch.Views.find(model, viewName);
  if (view == null) {
    throw Error(`No view with name ${viewName} was found`);
  }
  const fieldMap = new Map<string, Field<ModeRole>>();
  const naResult = handleInvalidFormulas(view, encoding);

  encoding = naResult.encoding;
  model = Switch.Models.make(viewName, [view]);

  if (encoding.marks.channel === '*') {
    makeMarks(model, viewName, encoding, fieldMap, encoding.marks.properties);
  }

  const names = naResult.names;
  const fields = Array.from(fieldMap.values()).map((field): Field<ModeRole> => {
    const name = names.get(field.alias) ?? field.name;
    return {
      ...field,
      name,
      formula: { ...field.formula, source: escapeFieldName(field.formula.source) },
    } as Field<ModeRole>;
  });

  const sorts = makeTableSorts(model, viewName, encoding, fieldMap);
  const filters = makeFilters(model, viewName, encoding, fieldMap);

  return RecordsQuery.make({
    fields,
    sorts,
    filters,
    limit,
    offset,
  });
}

/**
 * Result of separating fields by type
 */
interface SeparatedFieldsResult {
  /** Encoding with only scalar fields (aggregate fields removed, invalid replaced) */
  scalarEncoding: Encoding;
  /** Names map for scalar fields that were modified (e.g., invalid formulas) */
  scalarNames: Map<string, string>;
  /** Aggregate calc fields to be computed via PIVOT query */
  aggregateFields: Field<ModeRole>[];
  /** Map of aggregate field alias to display name */
  aggregateNames: Map<string, string>;
}

/**
 * Separates encoding fields into scalar and aggregate categories.
 * - Scalar fields stay in the encoding for the TABLE query
 * - Aggregate calc fields are extracted for a separate PIVOT query
 * - Invalid formulas are replaced with "#UNDEFINED"
 * - Analytic fields are currently replaced with "N/A" (not yet supported)
 */
function separateFieldsByType(
  adapter: Adapter<ModeRole>,
  model: Model<ModeRole>,
  view: View<ModeRole>,
  viewName: string,
  encoding: Encoding
): SeparatedFieldsResult {
  const scalarNames = new Map<string, string>();
  const aggregateFields: Field<ModeRole>[] = [];
  const aggregateNames = new Map<string, string>();
  const batch = batchEncoding(encoding);
  const encFields = collectEncFields(encoding, '*');

  for (const encField of encFields) {
    const compiled = CompiledField.fromEncField(view, encField);
    const id = encField.id as string;

    // Names/Values shouldn't be here.. skip
    if (compiled.isSystem) {
      continue;
    }

    // Invalid formula (references missing column, etc.)
    if (!compiled.isValid) {
      const displayName = encField.formula.replace(/^\[|\]$/g, '');
      scalarNames.set(id, displayName);
      batch.updateField({
        id,
        formula: '"#UNDEFINED"',
        variable: { type: VariableType.DISCRETE },
      });
      continue;
    }

    // Check if this is a non-scalar (aggregate or analytic) formula
    if (!compiled.isAggregateable) {
      // Aggregate calc field - extract for PIVOT query
      if (compiled.isAggregateStage) {
        // Create a field for the aggregate PIVOT query
        // Note: Type assertion needed due to version mismatch between @mode-vegas/vegas and @mode-switch/express
        const aggField = Switch.field(
          model,
          viewName,
          encField.formula,
          Switch.Compat.fromVegasVarType(encField.variable.type),
          id,
          encField.frame as Parameters<typeof Switch.field>[5]
        );
        if (aggField) {
          aggregateFields.push(aggField);
          aggregateNames.set(id, compiled.displayName);
        }

        // Remove from encoding (will be stitched back in results)
        batch.removeField(id);
      } else {
        // Analytic calc field - not yet supported, replace with "N/A"
        scalarNames.set(id, compiled.displayName);
        batch.updateField({
          id,
          formula: '"N/A"',
          variable: { type: VariableType.DISCRETE },
        });
      }
    }
  }

  return {
    scalarEncoding: batch.complete().encoding,
    scalarNames,
    aggregateFields,
    aggregateNames,
  };
}

/**
 * Handles only invalid formulas (for backward compatibility).
 * Replaces invalid formulas with "#UNDEFINED".
 */
function handleInvalidFormulas(view: View<ModeRole>, encoding: Encoding) {
  const names = new Map<string, string>();
  const batch = batchEncoding(encoding);
  const encFields = collectEncFields(encoding, '*');

  for (const encField of encFields) {
    const compiled = CompiledField.fromEncField(view, encField);
    const id = encField.id as string;

    if (compiled.isSystem) {
      continue;
    }

    // Invalid formula
    if (!compiled.isValid) {
      const displayName = encField.formula.replace(/^\[|\]$/g, '');
      names.set(id, displayName);
      batch.updateField({
        id,
        formula: '"#UNDEFINED"',
        variable: { type: VariableType.DISCRETE },
      });
      continue;
    }

    // Aggregate/analytic fields can't be used in records queries — substitute "N/A"
    if (!compiled.isAggregateable) {
      names.set(id, compiled.displayName);
      batch.updateField({
        id,
        formula: '"N/A"',
        variable: { type: VariableType.DISCRETE },
      });
    }
  }

  return {
    encoding: batch.complete().encoding,
    names,
  };
}
